import { TWO_FACTOR_CONFIG } from '@/config/twoFactorConfig';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Code,
  Grid,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useState } from 'react';

interface RecoveryCodesModalProps {
  isOpen: boolean;
  onClose: () => void;
  recoveryCodes: string[];
  onConfirm: () => void;
}

export const RecoveryCodesModal: React.FC<RecoveryCodesModalProps> = ({
  isOpen,
  onClose,
  recoveryCodes,
  onConfirm,
}) => {
  const toast = useToast();
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [hasConfirmed, setHasConfirmed] = useState(false);

  const handleDownload = () => {
    const content = [
      `${TWO_FACTOR_CONFIG.APP_NAME} Recovery Codes`,
      '',
      'Keep these codes safe. Each code can only be used once.',
      'Store them in a secure location like a password manager.',
      '',
      'Recovery Codes:',
      '',
      ...recoveryCodes,
      '',
      `Generated: ${new Date().toISOString()}`,
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${TWO_FACTOR_CONFIG.APP_NAME.toLowerCase()}-recovery-codes.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setHasDownloaded(true);
    toast({
      title: 'Downloaded',
      description: 'Recovery codes saved to file.',
      status: 'success',
      duration: 2000,
      position: 'top',
    });
  };

  const handleCopy = () => {
    const codesText = recoveryCodes.join('\n');
    navigator.clipboard.writeText(codesText);

    setHasDownloaded(true);
    toast({
      title: 'Copied',
      description: 'Recovery codes copied to clipboard.',
      status: 'success',
      duration: 2000,
      position: 'top',
    });
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent px={3} py={3}>
        <ModalHeader>Save Your Recovery Codes</ModalHeader>

        <ModalBody>
          <VStack spacing={6} align="stretch">
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">Important</Text>
                <Text fontSize="sm">
                  These codes are the only way to access your account if you lose your authenticator
                  device. Each code can only be used once.
                </Text>
              </Box>
            </Alert>

            <Box bg="gray.800" p={4} borderRadius="md" border="1px solid" borderColor="gray.600">
              <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                {recoveryCodes.map((code, index) => (
                  <Code
                    key={index}
                    p={2}
                    bg="gray.700"
                    color="white"
                    borderRadius="sm"
                    textAlign="center"
                    fontFamily="mono"
                    fontSize="sm"
                  >
                    {code}
                  </Code>
                ))}
              </Grid>
            </Box>

            <VStack spacing={3}>
              <Button w="full" onClick={handleDownload} variant="outline">
                Download as File
              </Button>
              <Button w="full" onClick={handleCopy} variant="outline">
                Copy to Clipboard
              </Button>
            </VStack>

            {hasDownloaded && (
              <Checkbox
                isChecked={hasConfirmed}
                onChange={(e) => setHasConfirmed(e.target.checked)}
                colorScheme="teal"
              >
                <Text fontSize="sm">I have saved my recovery codes in a safe place</Text>
              </Checkbox>
            )}

            {!hasDownloaded && (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  Please download or copy your recovery codes before continuing.
                </Text>
              </Alert>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="primary"
            onClick={handleConfirm}
            isDisabled={!hasDownloaded || !hasConfirmed}
            fontWeight="bold"
          >
            I've Saved My Codes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
